import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import sqliteImmediateStorage from "./sqliteImmediateStorage";

// DI-05: Simple mutex to prevent concurrent read-modify-write on documentSequence.
// Even in single-tab, auto-sync + manual order can race.
let _docSeqLock = false;
let _preorderSeqLock = false;
let _cancelledSeqLock = false;
let _parkedBillLock = false;

const DEFAULTS = {
  tillSession: null,
  documentSequence: 0,
  preorderSequence: null, // { sequenceId, name, prefix, suffix, nextNo }
  cancelledOrderSequence: null, // { sequenceId, name, prefix, suffix, nextNo }
  parkedBillCounter: 0,
  locked: false,
  unlink: false,
  nextBusinessDate: null,
  parkedList: [],
  lastUpdatedTime: null,
  // Mid-transaction recovery — persisted so payment resumes after crash/reload
  paymentInProgress: false,
  paymentSnapshot: null, // { selectedMethod, amount, partialPayments }
};

const useSessionStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        ...DEFAULTS,
        _hydrated: false,

        // Actions
        setTillSession: (session) => set({ tillSession: session }),
        updateTillSession: (updates) =>
          set((state) => ({
            tillSession: state.tillSession ? { ...state.tillSession, ...updates } : updates,
          })),

        setDocumentSequence: (seq) => set({ documentSequence: seq }),
        incrementDocumentSequence: () => {
          if (_docSeqLock) {
            let waited = 0;
            while (_docSeqLock && waited < 50) waited++;
            if (_docSeqLock) console.error("[DI-05] Document sequence lock not released — forcing through");
          }
          _docSeqLock = true;
          try {
            const current = get().documentSequence || 0;
            const next = current + 1;
            set({ documentSequence: next });
            return next;
          } finally {
            _docSeqLock = false;
          }
        },

        setPreorderSequence: (seq) => set({ preorderSequence: seq }),
        incrementPreorderSequence: () => {
          if (_preorderSeqLock) {
            let waited = 0;
            while (_preorderSeqLock && waited < 50) waited++;
            if (_preorderSeqLock) console.error("[DI-05] Preorder sequence lock not released — forcing through");
          }
          _preorderSeqLock = true;
          try {
            const current = get().preorderSequence;
            if (!current) return null;
            const next = (current.nextNo || 0) + 1;
            set({ preorderSequence: { ...current, nextNo: next } });
            return next;
          } finally {
            _preorderSeqLock = false;
          }
        },

        setCancelledOrderSequence: (seq) => set({ cancelledOrderSequence: seq }),
        incrementCancelledOrderSequence: () => {
          if (_cancelledSeqLock) {
            let waited = 0;
            while (_cancelledSeqLock && waited < 50) waited++;
            if (_cancelledSeqLock) console.error("[DI-05] Cancelled sequence lock not released — forcing through");
          }
          _cancelledSeqLock = true;
          try {
            const current = get().cancelledOrderSequence;
            if (!current) return null;
            const next = (current.nextNo || 0) + 1;
            set({ cancelledOrderSequence: { ...current, nextNo: next } });
            return next;
          } finally {
            _cancelledSeqLock = false;
          }
        },

        incrementParkedBillCounter: () => {
          if (_parkedBillLock) {
            let waited = 0;
            while (_parkedBillLock && waited < 50) waited++;
            if (_parkedBillLock) console.error("[DI-05] Parked bill counter lock not released — forcing through");
          }
          _parkedBillLock = true;
          try {
            const current = get().parkedBillCounter || 0;
            const next = current + 1;
            set({ parkedBillCounter: next });
            return next;
          } finally {
            _parkedBillLock = false;
          }
        },

        setLocked: (val) => set({ locked: val }),
        setUnlink: (val) => set({ unlink: val }),
        setNextBusinessDate: (date) => set({ nextBusinessDate: date }),
        setLastUpdatedTime: (time) => set({ lastUpdatedTime: time }),

        // Mid-transaction recovery
        setPaymentInProgress: (val) => set({ paymentInProgress: val }),
        setPaymentSnapshot: (snapshot) => set({ paymentSnapshot: snapshot }),
        clearPaymentState: () => set({ paymentInProgress: false, paymentSnapshot: null }),

        setParkedList: (list) => set({ parkedList: list }),
        addParkedBill: (bill) =>
          set((state) => {
            const list = [...(state.parkedList || []), bill];
            if (list.length > 100) list.shift();
            return { parkedList: list };
          }),
        removeParkedBill: (key) =>
          set((state) => ({
            parkedList: (state.parkedList || []).filter((b) => b.parkedDocNo !== key && b.key !== key),
          })),

        clearSession: () => set({ ...DEFAULTS }),
      }),
      {
        name: "sessionStore",
        storage: createJSONStorage(() => sqliteImmediateStorage),
        skipHydration: true,
        partialize: (state) => ({
          tillSession: state.tillSession,
          documentSequence: state.documentSequence,
          preorderSequence: state.preorderSequence,
          cancelledOrderSequence: state.cancelledOrderSequence,
          parkedBillCounter: state.parkedBillCounter,
          locked: state.locked,
          unlink: state.unlink,
          nextBusinessDate: state.nextBusinessDate,
          parkedList: state.parkedList,
          lastUpdatedTime: state.lastUpdatedTime,
        }),
        merge: (persisted, current) => ({
          ...current,
          ...DEFAULTS,
          ...(persisted || {}),
          _hydrated: true,
        }),
      }
    )
  )
);

export default useSessionStore;
